import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import {
  hydrateResumeJourneyState,
  type ResumeSessionResponse,
} from "@/D2C/features/auth/utils/resumeJourney";
import { defaultAdditionalInfoValues } from "@/D2C/features/car-quote/redux/car-quote/carQuoteSlice";
import { updateCarSessionStep } from "@/D2C/features/car-quote/services/service";
import {
  buildCarBuyNowBindPayload,
  buildCarSelectedQuotePayload,
} from "@/D2C/features/car-quote/utils/carQuotePayloadsUtils";
import { defaultHealthAdditionalInfoValues } from "@/D2C/features/health-quote/redux/health-quote/healthQuoteSlice";
import { updateHealthSessionStep } from "@/D2C/features/health-quote/services/service";
import {
  buildHealthBuyNowBindPayload,
  buildHealthSelectedQuotePayload,
} from "@/D2C/features/health-quote/utils/healthQuotePayloadsUtils";
import { defaultLifePlanDetailsFormValues } from "@/D2C/features/life-quote/redux/lifeQuoteSlice";
import { updateLifeSessionStep } from "@/D2C/features/life-quote/services/service";
import {
  buildLifeBuyNowBindPayload,
  buildLifeSelectedQuotePayload,
} from "@/D2C/features/life-quote/utils/lifeQuotePayloadsUtils";
import { updateTravelSessionStep } from "@/D2C/features/travel-quote/services/service";
import {
  buildTravelBuyNowBindPayload,
  buildTravelSelectedQuotePayload,
} from "@/D2C/features/travel-quote/utils/travelQuotePayloadsUtils";
import { LOB_IDS } from "@/shared/common/static";
import { Button } from "@/shared/components";
import { SESSION_STEP_KEYS } from "@/shared/constants/sessionStepKeys";
import { showToast } from "@/shared/lib/toast";
import { setSession } from "@/shared/redux/reducers/authSlice";
import { clearQuoteList, setQuoteList, sortQuotes } from "@/shared/redux/reducers/commonSlice";
import type { AppDispatch } from "@/shared/redux/store/store";
import { bindquote } from "@/shared/services/bindquote";
import { mergeStructuredOcrDocuments } from "@/shared/utils/documentOcr";

import { StatusAlert } from "@/shared/components/StatusAlert";
import { useClientLinks } from "../hooks/useClientLinks";
import { useHealthQuotes } from "../hooks/useHealthQuotes";
import { useLifeQuotes } from "../hooks/useLifeQuotes";
import { useMotorQuotes } from "../hooks/useMotorQuotes";
import { usePostQuote } from "../hooks/usePostQuote";
import { usePreQuoteFormChange } from "../hooks/usePreQuoteFormChange";
import { useQuoteRefs } from "../hooks/useQuoteRefs";
import { useTravelQuotes } from "../hooks/useTravelQuotes";
import { applyB2CSessionHydrationOverrides } from "../utils/b2cSessionHydrationOverrides";
import type { SessionIds } from "../utils/getSessionIds";
import {
  getProductFromSession,
  getQuoteViewUiState,
  getSessionContact,
} from "../utils/quoteViewState";
import { withB2CSelectedQuoteCommission } from "../utils/selectedQuotePayload";
import { hydrateUploadedDocumentsFromSession } from "../utils/sessionDocumentHydration";
import { PostQuoteSection } from "./PostQuoteSection";
import { PreQuoteForms } from "./PreQuoteForms";
import { QuotesListSection } from "./QuotesListSection";

type Props = {
  sessionData: ResumeSessionResponse;
};

const isExpiredStatus = (value: unknown) =>
  typeof value === "string" && ["expired", "failed"].includes(value.trim().toLowerCase());

const isFailedStatus = (value: unknown) =>
  typeof value === "string" && ["failed"].includes(value.trim().toLowerCase());

export default function ViewQuotePage({ sessionData }: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const refs = useQuoteRefs();

  const product = getProductFromSession(sessionData);
  const hydration = useMemo(
    () => applyB2CSessionHydrationOverrides(sessionData, hydrateResumeJourneyState(sessionData)),
    [sessionData]
  );
  const initialUi = useMemo(() => getQuoteViewUiState(sessionData), [sessionData]);
  const isExpiredQuoteJourney = useMemo(() => {
    return (
      isExpiredStatus((sessionData as any)?.get_quote_expiry) ||
      isExpiredStatus((sessionData as any)?.start_date_expiry) ||
      isExpiredStatus((sessionData as any)?.step_7_post_request?.payment_status)
    );
  }, [sessionData]);
  const isPaymentFailed = useMemo(
    () => isFailedStatus((sessionData as any)?.step_7_post_request?.payment_status),
    [sessionData]
  );
  const isJourneyReadOnly = initialUi.isPolicyIssued || isExpiredQuoteJourney;
  const sessionContact = useMemo(() => getSessionContact(sessionData), [sessionData]);
  const hasRenderablePrefetchedQuotes = useMemo(
    () =>
      initialUi.prefetchedQuotes.some((quote: any) => {
        const amount = Number(
          quote?.plan?.price?.amount_with_vat ??
            quote?.plan?.price?.amount ??
            quote?.price?.amount_with_vat ??
            quote?.price?.amount
        );
        return (
          Boolean(quote?.insurer?.name && quote?.plan?.name) &&
          Number.isFinite(amount) &&
          amount > 0
        );
      }),
    [initialUi.prefetchedQuotes]
  );
  const sessionUploadedDocuments = useMemo(
    () => hydrateUploadedDocumentsFromSession(sessionData, hydration.lobId),
    [sessionData, hydration.lobId]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetchKey, setFetchKey] = useState("resume");
  const [showQuoteList, setShowQuoteList] = useState(initialUi.showQuoteList);
  const [selectedQuote, setSelectedQuote] = useState<any>(initialUi.selectedQuote);
  const [isPostDataReady, setIsPostDataReady] = useState(initialUi.isPostDataReady);
  const [isUploadConfirmed, setIsUploadConfirmed] = useState(false);
  const [healthAccordionOpenVersion, setHealthAccordionOpenVersion] = useState(0);
  const [healthAccordionInvalidSections, setHealthAccordionInvalidSections] = useState({
    basic: false,
    habits: false,
    quick: false,
  });
  const postQuoteAutoSaveInFlightRef = useRef(false);
  const [activeSessionIds, setActiveSessionIds] = useState<SessionIds | null>({
    clientId: sessionData.client_id ?? sessionData.client?.id ?? null,
    sessionId: sessionData.id ?? null,
    session: sessionData,
  });

  const [travelUploadedDocuments, setTravelUploadedDocuments] = useState(
    sessionUploadedDocuments.travel
  );
  const [carUploadedDocuments, setCarUploadedDocuments] = useState(sessionUploadedDocuments.car);
  const [healthUploadedDocuments, setHealthUploadedDocuments] = useState(
    sessionUploadedDocuments.health
  );

  const resolvedSessionIds = useMemo(
    () => ({
      clientId: activeSessionIds?.clientId ?? sessionData.client_id ?? null,
      sessionId: activeSessionIds?.sessionId ?? sessionData.id ?? null,
    }),
    [activeSessionIds, sessionData]
  );

  const preQuoteDefaults = useMemo(() => {
    if (hydration.lobId === LOB_IDS.TRAVEL && hydration.travel) {
      return {
        travelPersonal: hydration.travel.personalDetails,
        travelDetails: hydration.travel.travelDetails,
      };
    }
    if (hydration.lobId === LOB_IDS.MOTOR && hydration.car) {
      return {
        carPersonal: hydration.car.personalDetails,
        carVehicle: hydration.car.vehicleDetails,
        carDriver: hydration.car.driverDetails,
      };
    }
    if (hydration.lobId === LOB_IDS.HEALTH && hydration.health) {
      return {
        healthPersonal: hydration.health.personalDetails,
        healthDetails: hydration.health.healthDetails,
        healthHabits: hydration.health.healthHabits,
        healthQuickQuestions: hydration.health.quickQuestions,
      };
    }
    if (hydration.lobId === LOB_IDS.LIFE && hydration.life) {
      return {
        lifePersonal: hydration.life.personalDetails,
        lifePlanDetails: hydration.life.planDetails,
      };
    }
    return undefined;
  }, [hydration]);

  const postQuoteDefaults = useMemo(() => {
    if (hydration.lobId === LOB_IDS.TRAVEL && hydration.travel) {
      return {
        travelAdditionalDefaults: hydration.travel.additionalInfo,
        travelDocumentDefaults: hydration.travel.documentUpload,
      };
    }
    if (hydration.lobId === LOB_IDS.MOTOR && hydration.car) {
      return {
        carAdditionalDefaults: hydration.car.additionalInfo,
        carDocumentDefaults: hydration.car.documentUpload,
      };
    }
    if (hydration.lobId === LOB_IDS.HEALTH && hydration.health) {
      return {
        healthAdditionalDefaults: hydration.health.additionalInfo,
        healthDocumentDefaults: hydration.health.documentUpload,
      };
    }
    if (hydration.lobId === LOB_IDS.LIFE && hydration.life) {
      return {
        travelAdditionalDefaults: hydration.life.additionalInfo,
        travelDocumentDefaults: hydration.life.documentUpload,
      };
    }
    return undefined;
  }, [hydration]);

  const travelStructuredOcrData = useMemo(
    () => mergeStructuredOcrDocuments(travelUploadedDocuments),
    [travelUploadedDocuments]
  );
  const carStructuredOcrData = useMemo(
    () => mergeStructuredOcrDocuments(carUploadedDocuments),
    [carUploadedDocuments]
  );
  const healthStructuredOcrData = useMemo(
    () => mergeStructuredOcrDocuments(healthUploadedDocuments),
    [healthUploadedDocuments]
  );

  const selectedAddOns = useMemo(
    () =>
      selectedQuote?.sections
        ?.find((s: any) => s?.title === "Add-Ons")
        ?.items?.filter((i: any) => i?.is_checked)
        ?.map((i: any) => i?.label) || [],
    [selectedQuote]
  );

  const createTravelQuotes = useTravelQuotes(refs, setActiveSessionIds);
  const createMotorQuotes = useMotorQuotes(refs, setActiveSessionIds);
  const createHealthQuotes = useHealthQuotes(refs, setActiveSessionIds, (invalidSections) => {
    setHealthAccordionInvalidSections(invalidSections);
    setHealthAccordionOpenVersion((prev) => prev + 1);
  });
  const createLifeQuotes = useLifeQuotes(refs, setActiveSessionIds);

  const {
    isPostSubmitting,
    handleSubmitPostQuote,
    handleSubmitUploadOnly,
    handleSubmitAdditionalOnly,
  } = usePostQuote({
    lobId: product?.lobId ?? hydration.lobId,
    refs,
    selectedQuote,
    selectedAddOns,
    clientId: resolvedSessionIds.clientId!,
    sessionId: resolvedSessionIds.sessionId!,
    travelUploadedDocuments,
    carUploadedDocuments,
    healthUploadedDocuments,
    travelStructuredOcrData,
    carStructuredOcrData,
    healthStructuredOcrData,
  });

  const {
    isSendingUploadLink,
    isSendingPaymentLink,
    handleSendUploadLinkToClient,
    handleSendPaymentLinkToClient,
  } = useClientLinks({
    lobId: product?.lobId ?? hydration.lobId,
    refs,
    selectedQuote,
    clientId: resolvedSessionIds.clientId,
    sessionId: resolvedSessionIds.sessionId,
    isPostDataReady,
    sessionContact,
  });

  const { hasPreQuoteChanged, capturePreQuoteBaseline } = usePreQuoteFormChange(
    refs,
    product?.lobId ?? hydration.lobId,
    `${sessionData.id}-${hydration.lobId}`
  );

  useEffect(() => {
    if (!hasPreQuoteChanged || selectedQuote) return;

    setShowQuoteList(false);
    dispatch(clearQuoteList());
  }, [dispatch, hasPreQuoteChanged, selectedQuote]);

  useEffect(() => {
    dispatch(
      setSession({
        client: { id: String(sessionData.client_id ?? sessionData.client?.id ?? "") },
        session_data: { id: String(sessionData.id ?? "") },
        session_quotes: sessionData,
      })
    );

    const prefetched = initialUi.prefetchedQuotes;
    if (prefetched.length > 0) {
      dispatch(clearQuoteList());
      prefetched.forEach((quote) => dispatch(setQuoteList(quote as any)));
      dispatch(sortQuotes("sequence-asc"));
    }
  }, [dispatch, initialUi.prefetchedQuotes, sessionData]);

  const handleGetQuotes = async () => {
    if (!product?.lobId || isJourneyReadOnly) return;

    setIsSubmitting(true);
    setShowQuoteList(false);
    setSelectedQuote(null);
    setIsPostDataReady(false);

    try {
      const fn =
        product?.lobId === LOB_IDS.TRAVEL
          ? createTravelQuotes
          : product?.lobId === LOB_IDS.MOTOR
            ? createMotorQuotes
            : product?.lobId === LOB_IDS.LIFE
              ? createLifeQuotes
              : createHealthQuotes;

      if (await fn()) {
        setShowQuoteList(true);
        setFetchKey(String(Date.now()));
        capturePreQuoteBaseline();
      }
    } catch (error: any) {
      showToast(
        error?.response?.data?.message || error?.message || "Failed to refresh quotes.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectQuote = useCallback(
    async (quote: any) => {
      if (isJourneyReadOnly) {
        return;
      }

      const currentLobId = product?.lobId ?? hydration.lobId;
      const { clientId, sessionId } = resolvedSessionIds;

      if (!clientId || !sessionId) {
        showToast("Session not found. Please get quotes again.", "error");
        return;
      }

      try {
        if (currentLobId === LOB_IDS.TRAVEL) {
          await bindquote(
            buildTravelBuyNowBindPayload({
              selectedQuote: quote,
              clientId,
              sessionId,
              travelDetails:
                (refs.travelDetailsRef.current?.getValues() as any) ??
                preQuoteDefaults?.travelDetails,
              additionalInfo: postQuoteDefaults?.travelAdditionalDefaults ?? {},
            })
          );

          await updateTravelSessionStep({
            step: SESSION_STEP_KEYS.SELECTED_QUOTE,
            payload: withB2CSelectedQuoteCommission(buildTravelSelectedQuotePayload(quote), quote),
            clientId,
            sessionId,
            lobId: LOB_IDS.TRAVEL,
          });
        } else if (currentLobId === LOB_IDS.MOTOR) {
          await bindquote(
            buildCarBuyNowBindPayload({
              quote,
              clientId,
              sessionId,
              selectedAddOns,
              personalDetails:
                (refs.carPersonalRef.current?.getValues() as any) ?? preQuoteDefaults?.carPersonal,
              driverDetails:
                (refs.carDriverRef.current?.getValues() as any) ?? preQuoteDefaults?.carDriver,
              additionalInfo:
                postQuoteDefaults?.carAdditionalDefaults ?? defaultAdditionalInfoValues,
              lob_id: LOB_IDS.MOTOR,
            })
          );

          await updateCarSessionStep({
            step: SESSION_STEP_KEYS.SELECTED_QUOTE,
            payload: withB2CSelectedQuoteCommission(buildCarSelectedQuotePayload(quote), quote),
            clientId,
            sessionId,
            lobId: LOB_IDS.MOTOR,
          });
        } else if (currentLobId === LOB_IDS.HEALTH) {
          await bindquote(
            buildHealthBuyNowBindPayload({
              selectedQuote: quote,
              clientId,
              sessionId,
              personalDetails:
                (refs.healthPersonalRef.current?.getValues() as any) ??
                preQuoteDefaults?.healthPersonal,
              healthDetails:
                (refs.healthDetailsRef.current?.getValues() as any) ??
                preQuoteDefaults?.healthDetails,
              additionalInfo:
                postQuoteDefaults?.healthAdditionalDefaults ?? defaultHealthAdditionalInfoValues,
              lob_id: LOB_IDS.HEALTH,
            })
          );

          await updateHealthSessionStep({
            step: SESSION_STEP_KEYS.SELECTED_QUOTE,
            payload: withB2CSelectedQuoteCommission(buildHealthSelectedQuotePayload(quote), quote),
            clientId,
            sessionId,
            lobId: LOB_IDS.HEALTH,
          });
        } else if (currentLobId === LOB_IDS.LIFE) {
          await bindquote(
            buildLifeBuyNowBindPayload({
              quote,
              clientId,
              sessionId,
              personalDetails:
                (refs.lifePersonalRef.current?.getValues() as any) ??
                preQuoteDefaults?.travelPersonal,
              termDetails:
                (refs.lifePlanDetailsRef.current?.getValues() as any) ??
                preQuoteDefaults?.lifePlanDetails ??
                defaultLifePlanDetailsFormValues,
              additionalInfo: postQuoteDefaults?.travelAdditionalDefaults ?? {},
            })
          );

          await updateLifeSessionStep({
            step: SESSION_STEP_KEYS.SELECTED_QUOTE,
            payload: withB2CSelectedQuoteCommission(buildLifeSelectedQuotePayload(quote), quote),
            clientId,
            sessionId,
            lobId: LOB_IDS.LIFE,
          });
        }
      } catch (error: any) {
        showToast(
          error?.response?.data?.message || error?.message || "Failed to select quote.",
          "error"
        );
        return;
      }

      const resolvedQuoteId =
        quote?.quote_id ?? quote?.id ?? quote?.quoteId ?? sessionData.id ?? null;

      setSelectedQuote({
        ...quote,
        quote_id: resolvedQuoteId,
        id: quote?.id ?? resolvedQuoteId,
      });
      setShowQuoteList(false);
      setIsPostDataReady(false);
      showToast("Quote selected. Continue with post-quote steps.", "success");
      requestAnimationFrame(() =>
        refs.postQuoteSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      );
    },
    [
      hydration.lobId,
      isJourneyReadOnly,
      product?.lobId,
      refs.postQuoteSectionRef,
      resolvedSessionIds,
      sessionData.id,
    ]
  );

  const handlePostConfirmationChange = useCallback(
    async (type: "upload" | "additional", checked: boolean) => {
      if (isJourneyReadOnly) {
        return false;
      }

      if (postQuoteAutoSaveInFlightRef.current || isPostSubmitting) {
        return checked ? false : true;
      }

      if (checked) {
        postQuoteAutoSaveInFlightRef.current = true;
        let saved = false;
        try {
          if (type === "upload") {
            saved = await handleSubmitUploadOnly();
          } else {
            saved = await handleSubmitAdditionalOnly();
          }
        } finally {
          postQuoteAutoSaveInFlightRef.current = false;
        }

        if (!saved) {
          setIsPostDataReady(false);
          showToast(
            type === "upload"
              ? "Please complete required upload details before confirming document upload."
              : "Please complete required additional details before confirming.",
            "error"
          );
          return false;
        }
      }

      const currentLobId = product?.lobId ?? hydration.lobId;
      let isUploadConfirmed = false;
      let isAdditionalConfirmed = false;

      if (currentLobId === LOB_IDS.TRAVEL) {
        const uploadValues = refs.travelDocumentUploadRef.current?.getValues();
        const additionalValues = refs.travelAdditionalInfoRef.current?.getValues();
        // Trust the just-toggled checkbox value instead of a possibly stale getValues() read.
        isUploadConfirmed = type === "upload" ? checked : uploadValues?.confirmDocuments === true;
        isAdditionalConfirmed =
          type === "additional" ? checked : additionalValues?.uaeResidentConfirm === true;
      } else if (currentLobId === LOB_IDS.LIFE) {
        const uploadValues = refs.travelDocumentUploadRef.current?.getValues();
        isUploadConfirmed = type === "upload" ? checked : uploadValues?.confirmDocuments === true;
        // Life journey has no additional details section, so upload confirmation alone gates readiness.
        isAdditionalConfirmed = true;
      } else if (currentLobId === LOB_IDS.MOTOR) {
        const uploadValues = refs.carDocumentUploadRef.current?.getValues();
        const additionalValues = refs.carAdditionalInfoRef.current?.getValues();
        isUploadConfirmed = type === "upload" ? checked : uploadValues?.confirmDocuments === true;
        isAdditionalConfirmed =
          type === "additional" ? checked : additionalValues?.authorizationAccepted === true;
      } else if (currentLobId === LOB_IDS.HEALTH) {
        const uploadValues = refs.healthDocumentUploadRef.current?.getValues();
        const additionalValues = refs.healthAdditionalInfoRef.current?.getValues();
        isUploadConfirmed = type === "upload" ? checked : uploadValues?.confirmDocuments === true;
        isAdditionalConfirmed =
          type === "additional" ? checked : additionalValues?.uaeResidentConfirm === true;
      }

      setIsUploadConfirmed(isUploadConfirmed);

      if (!isUploadConfirmed || !isAdditionalConfirmed) {
        setIsPostDataReady(false);
        return true;
      }

      setIsPostDataReady(true);
      return true;
    },
    [
      handleSubmitAdditionalOnly,
      handleSubmitUploadOnly,
      hydration.lobId,
      isJourneyReadOnly,
      isPostSubmitting,
      product?.lobId,
      refs,
    ]
  );

  const assignedTo =
    (sessionData as any)?.user?.full_name ||
    (sessionData as any)?.assigned_user?.full_name ||
    "Unassigned";

  const productWithLob = { ...product, lobId: product?.lobId };
  const showPostSection = Boolean(selectedQuote);

  return (
    <div className="space-y-2">
      {isExpiredQuoteJourney && (
        <StatusAlert
          variant="warning"
          message={
            isPaymentFailed
              ? "Payment failed. Please send the payment link to the client again."
              : "This quote is expired. The full journey is available in read-only mode."
          }
          dismissible={true}
        />
      )}
      <PreQuoteForms
        product={productWithLob}
        refs={refs}
        isReadOnly={isJourneyReadOnly}
        defaultValues={preQuoteDefaults}
        healthAccordionOpenVersion={healthAccordionOpenVersion}
        healthAccordionInvalidSections={healthAccordionInvalidSections}
        forceCloseAccordions={showQuoteList || showPostSection}
      />
      {!isJourneyReadOnly && product?.available ? (
        <div className="flex justify-end">
          <Button
            isLoading={isSubmitting}
            disabled={isSubmitting || (!hasPreQuoteChanged && hasRenderablePrefetchedQuotes)}
            loadingText="Getting quotes..."
            onClick={handleGetQuotes}
          >
            Get Quotes
          </Button>
        </div>
      ) : null}

      {showQuoteList && !selectedQuote ? (
        <QuotesListSection
          product={productWithLob}
          fetchKey={`view-${product?.key}-${fetchKey}`}
          onSelectQuote={handleSelectQuote}
          isReadOnly={isJourneyReadOnly}
          clientEmail={sessionContact.email}
          clientName={sessionContact.customerName}
        />
      ) : null}

      {showPostSection ? (
        <PostQuoteSection
          ref={refs.postQuoteSectionRef}
          product={productWithLob}
          refs={refs}
          selectedQuote={selectedQuote}
          isPostSubmitting={isPostSubmitting}
          travelUploadedDocuments={travelUploadedDocuments}
          carUploadedDocuments={carUploadedDocuments}
          healthUploadedDocuments={healthUploadedDocuments}
          travelStructuredOcrData={travelStructuredOcrData}
          carStructuredOcrData={carStructuredOcrData}
          healthStructuredOcrData={healthStructuredOcrData}
          onSetTravelUploads={setTravelUploadedDocuments}
          onSetCarUploads={setCarUploadedDocuments}
          onSetHealthUploads={setHealthUploadedDocuments}
          travelDetails={preQuoteDefaults?.travelDetails}
          onBack={() => {
            setSelectedQuote(null);
            // Always show list after exiting post-quote to avoid an empty screen state.
            setShowQuoteList(true);
            setIsPostDataReady(false);
            setIsUploadConfirmed(false);
          }}
          isPostDataReady={isPostDataReady}
          isSendingUploadLink={isSendingUploadLink}
          isSendingPaymentLink={isSendingPaymentLink}
          isReadOnly={isJourneyReadOnly}
          travelAdditionalDefaults={postQuoteDefaults?.travelAdditionalDefaults}
          travelDocumentDefaults={postQuoteDefaults?.travelDocumentDefaults}
          carAdditionalDefaults={postQuoteDefaults?.carAdditionalDefaults}
          carDocumentDefaults={postQuoteDefaults?.carDocumentDefaults}
          healthAdditionalDefaults={postQuoteDefaults?.healthAdditionalDefaults}
          healthDocumentDefaults={postQuoteDefaults?.healthDocumentDefaults}
          onSavePostData={async () => {
            if (isJourneyReadOnly) {
              return;
            }
            const success = await handleSubmitPostQuote();
            setIsPostDataReady(Boolean(success));
          }}
          onSendUploadLink={() => {
            if (isJourneyReadOnly) {
              return;
            }
            void handleSendUploadLinkToClient();
          }}
          onSendPaymentLink={() => {
            if (isJourneyReadOnly && !isPaymentFailed) {
              return;
            }
            void handleSendPaymentLinkToClient();
          }}
          onUploadConfirmChange={(checked) => {
            void handlePostConfirmationChange("upload", checked);
          }}
          onAdditionalConfirmChange={(checked) => {
            void handlePostConfirmationChange("additional", checked);
          }}
          showSaveButton={false}
          showSectionTitles={false}
          isUploadConfirmed={isUploadConfirmed}
          isPaymentFailed={isPaymentFailed}
        />
      ) : null}
    </div>
  );
}
